import React, { useEffect, useState } from 'react';
import { loadFormattedJson } from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditApi';
import { ScaleLogoLoading, SegmentedButtons, Divider, Banner } from '@scale/scaleui';
import { JsonTableView } from './JsonTableView';
import { useLookupObject } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { ReadOnlyGenericTask } from 'react-scale/corp/views/qm/QMLLMWorkerPerformancePage/QCDisputeFlow/ReadOnlyGenericTask';

interface ChatBulkAuditFormattedResponseViewerProps {
  attemptAuditId: string | undefined;
  taskId: string;
  setShowFeedbackBar: (showFeedbackBar: boolean) => void;
}

export function ChatBulkAuditFormattedResponseViewer({
  attemptAuditId,
  taskId,
  setShowFeedbackBar,
}: ChatBulkAuditFormattedResponseViewerProps) {
  const [formattedResponse, setFormattedResponse] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSxsView, setShowSxsView] = useState('table-json-only');
  const [recentTaskAttemptId, setRecentTaskAttemptId] = useState<string | null>(null);
  const [isAuditAttemptOutdated, setIsAuditAttemptOutdated] = useState(false);

  const { data: task } = useLookupObject(taskId);
  useEffect(() => {
    if (!attemptAuditId) {
      return;
    }
    setIsLoading(true);
    loadFormattedJson(attemptAuditId)
      .then(response => {
        setFormattedResponse(response.formattedResponse);
        setRecentTaskAttemptId(response.recentTaskAttemptId);
        setIsAuditAttemptOutdated(response.isAuditAttemptOutdated);
        setIsLoading(false);
        return response.formattedResponse;
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [attemptAuditId]);

  // TODO: show error if there are no rows
  return isLoading || !formattedResponse ? (
    <div className="flex justify-center items-center h-full">
      <ScaleLogoLoading size="lg" />
    </div>
  ) : (
    <div className="h-full w-full max-h-[77vh]">
      {isAuditAttemptOutdated && (
        <div className="w-full mb-3">
          <Banner
            variant={'warning'}
            title={`Please note that you are viewing data from the most recent attempt for this task (attempt ID: ${recentTaskAttemptId}), which is different from the attempt used in this audit.`}
            background="white"
          />
        </div>
      )}
      <div className="flex justify-center">
        <SegmentedButtons
          buttons={[
            { id: 'table-json-only', label: 'Table/Json Only' },
            { id: 'side-by-side', label: 'Side by Side' },
          ]}
          onSelect={id => {
            if (id === 'side-by-side') {
              setShowSxsView('side-by-side');
              setShowFeedbackBar(false);
            } else {
              setShowSxsView('table-json-only');
              setShowFeedbackBar(true);
            }
          }}
          selected={showSxsView}
          className="mb-4 mx-2"
        />
      </div>
      {showSxsView === 'side-by-side' ? (
        <div className="flex h-full w-full gap-4">
          <div className="w-1/2 flex-1 overflow-y-auto">
            {task && <ReadOnlyGenericTask taskIdOrtaskAttemptId={taskId} />}
            {!task && (
              <div className="flex justify-center items-center h-full">
                <ScaleLogoLoading size="lg" />
              </div>
            )}
          </div>
          <Divider direction="vertical" className="w-0.5" />
          <div className="w-1/2 flex-1 overflow-y-auto m-4">
            <JsonTableView
              formattedResponse={formattedResponse}
              showViewTypeToggle={true}
              tableValuesClassName="w-80"
            />
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto h-full">
          <JsonTableView
            formattedResponse={formattedResponse}
            showViewTypeToggle={true}
            tableValuesClassName="w-[40vw]"
          />
        </div>
      )}
    </div>
  );
}
